/**
 * @file Frontend callable wrappers for Shared Calendar workflows.
 */

import {
  createCalendarEventCallable,
  createCalendarReminderCallable,
  deleteCalendarEventCallable,
  deleteCalendarReminderCallable,
  handleApprovalActionCallable,
  previewLeaveRequestOverlapsCallable,
  revalidateApprovalOverlapsCallable,
  requestLeaveCancellationCallable,
  requestLeaveEditCallable,
  stopCalendarReminderRecurrenceCallable,
  submitLeaveRequestCallable,
  updateCalendarEventCallable,
  updateCalendarReminderCallable,
} from "../../app/firebase/callables.js";

/**
 * Creates a Shared Calendar event through the backend authority path.
 * @param {object} data Event payload.
 * @param {boolean} [notifyUsers=false] Whether to notify event users.
 * @returns {Promise<object>} Callable response data.
 */
export async function createCalendarEvent(data, notifyUsers = false) {
  const response = await createCalendarEventCallable({ data, notifyUsers });
  return response.data;
}

/**
 * Updates a Shared Calendar event through the backend authority path.
 * @param {string} eventId Event id.
 * @param {object} data Event payload.
 * @param {*} [expectedUpdatedAt=null] Optional concurrency timestamp.
 * @param {boolean} [notifyUsers=false] Whether to notify event users.
 * @returns {Promise<object>} Callable response data.
 */
export async function updateCalendarEvent(
    eventId,
    data,
    expectedUpdatedAt = null,
    notifyUsers = false,
) {
  const response = await updateCalendarEventCallable({
    eventId,
    data,
    expectedUpdatedAt,
    notifyUsers,
  });
  return response.data;
}

/**
 * Deletes a Shared Calendar event through the backend authority path.
 * @param {string} eventId Event id.
 * @param {*} [expectedUpdatedAt=null] Optional concurrency timestamp.
 * @param {boolean} [notifyUsers=false] Whether to notify event users.
 * @returns {Promise<object>} Callable response data.
 */
export async function deleteCalendarEvent(
    eventId,
    expectedUpdatedAt = null,
    notifyUsers = false,
) {
  const response = await deleteCalendarEventCallable({
    eventId,
    expectedUpdatedAt,
    notifyUsers,
  });
  return response.data;
}

/**
 * Creates a Shared Calendar reminder through the backend authority path.
 * @param {object} data Reminder payload.
 * @returns {Promise<object>} Callable response data.
 */
export async function createCalendarReminder(data) {
  const response = await createCalendarReminderCallable({ data });
  return response.data;
}

/**
 * Updates a Shared Calendar reminder through the backend authority path.
 * @param {string} reminderId Reminder id.
 * @param {object} data Reminder payload.
 * @param {*} [expectedUpdatedAt=null] Optional concurrency timestamp.
 * @returns {Promise<object>} Callable response data.
 */
export async function updateCalendarReminder(
    reminderId,
    data,
    expectedUpdatedAt = null,
) {
  const response = await updateCalendarReminderCallable({
    reminderId,
    data,
    expectedUpdatedAt,
  });
  return response.data;
}

/**
 * Deletes a Shared Calendar reminder through the backend authority path.
 * @param {string} reminderId Reminder id.
 * @param {*} [expectedUpdatedAt=null] Optional concurrency timestamp.
 * @param {boolean} [notifyUsers=false] Whether to notify reminder users.
 * @returns {Promise<object>} Callable response data.
 */
export async function deleteCalendarReminder(
    reminderId,
    expectedUpdatedAt = null,
    notifyUsers = false,
) {
  const response = await deleteCalendarReminderCallable({
    reminderId,
    expectedUpdatedAt,
    notifyUsers,
  });
  return response.data;
}

/**
 * Stops future recurrence for a Shared Calendar reminder series.
 * @param {string} reminderId Reminder id.
 * @param {*} [expectedUpdatedAt=null] Optional concurrency timestamp.
 * @returns {Promise<object>} Callable response data.
 */
export async function stopCalendarReminderRecurrence(
    reminderId,
    expectedUpdatedAt = null,
) {
  const response = await stopCalendarReminderRecurrenceCallable({
    reminderId,
    expectedUpdatedAt,
  });
  return response.data;
}

/**
 * Previews warning-only leave overlap payloads.
 * @param {object} data Leave request payload.
 * @param {string|null} [excludeLeaveRequestId=null] Leave id to ignore.
 * @returns {Promise<object>} Callable response data.
 */
export async function previewLeaveRequestOverlaps(
    data,
    excludeLeaveRequestId = null,
) {
  const response = await previewLeaveRequestOverlapsCallable({
    data,
    excludeLeaveRequestId,
  });
  return response.data;
}

/**
 * Submits a Leave request through the backend authority path.
 * @param {object} data Leave request payload.
 * @returns {Promise<object>} Callable response data.
 */
export async function submitLeaveRequest(data) {
  const response = await submitLeaveRequestCallable({ data });
  return response.data;
}

/**
 * Requests an approved Leave edit through the backend authority path.
 * @param {string} leaveRequestId Approved leave id.
 * @param {object} data Leave request payload.
 * @returns {Promise<object>} Callable response data.
 */
export async function requestLeaveEdit(leaveRequestId, data) {
  const response = await requestLeaveEditCallable({ leaveRequestId, data });
  return response.data;
}

/**
 * Requests approved Leave cancellation through the backend authority path.
 * @param {string} leaveRequestId Approved leave id.
 * @param {object} data Cancellation payload.
 * @returns {Promise<object>} Callable response data.
 */
export async function requestLeaveCancellation(leaveRequestId, data = {}) {
  const response = await requestLeaveCancellationCallable({
    leaveRequestId,
    data,
  });
  return response.data;
}

/**
 * Approves or rejects a Leave approval through the generic approval callable.
 * @param {string} approvalId Approval id.
 * @param {string} action Approval action.
 * @param {string|null} [comment=null] Decision comment.
 * @returns {Promise<object>} Callable response data.
 */
export async function handleLeaveApprovalAction(
    approvalId,
    action,
    comment = null,
) {
  const response = await handleApprovalActionCallable({
    approvalId,
    action,
    comment,
  });
  return response.data;
}

/**
 * Revalidates warning-only Leave approval overlaps against current backend state.
 * @param {string} approvalId Approval id.
 * @returns {Promise<object>} Callable response data.
 */
export async function revalidateApprovalOverlaps(approvalId) {
  const response = await revalidateApprovalOverlapsCallable({ approvalId });
  return response.data;
}
